import { AfterViewChecked, Directive, ElementRef, Input, Renderer2 } from '@angular/core';

/**
 * `aria-pressed` for a toggle button that also carries `mjButton`.
 *
 * WHY THIS EXISTS. `MJButtonDirective` owns `attr.aria-pressed` through a host
 * binding of its own — `@HostBinding('attr.aria-pressed') get ariaPressed() {
 * return this.toggleable ? String(this.selected) : null; }`
 * (node_modules/@memberjunction/ng-ui-components/dist/lib/button/button.directive.js:66).
 * Ivy runs a view's host bindings (`processHostBindingOpCodes`) AFTER its
 * template bindings, and an `attr` binding of `null` REMOVES the attribute — so
 * an author-written `[attr.aria-pressed]` on an `mjButton` is set and then
 * silently deleted on the same change-detection pass. That is why the detail
 * pages' `Save to board`/`Pin` buttons carried no `aria-pressed` at all (live
 * finding #28), despite binding it.
 *
 * The directive's own supported route — `[toggleable]="true" [selected]="…"` —
 * does emit the attribute, but it also brings `.mj-btn--selected`, a solid
 * `--mj-brand-primary` fill (button.scss:216). On these CTA rows that would make
 * `Saved to board` visually identical to the `Apply` button sitting next to it,
 * so the pressed state would read as a second primary action. This directive
 * takes the attribute without the fill: the pressed treatment stays the
 * `[variant]` the page already chooses.
 *
 * `ngAfterViewChecked` rather than a host binding of our own because host
 * bindings run in directive-matching order, and a module-declared directive is
 * matched BEFORE one imported from a library — so a host binding here would run
 * first and lose to `mjButton` exactly as the template binding does. View hooks
 * run at the end of `refreshView`, after `processHostBindingOpCodes`, which is
 * the one place a write reliably survives. The write is guarded on the last
 * value, so a steady state costs one comparison per pass.
 */
@Directive({
    standalone: false,
    selector: '[raPressed]',
})
export class RaPressedDirective implements AfterViewChecked {
    /** Lowercase alias so the selector doubles as the input, the same convention `mjButton`'s own inputs follow. */
    @Input({ required: true, alias: 'raPressed' }) Pressed = false;

    /** Last value written to the DOM, so an unchanged state writes nothing. */
    private written: string | null = null;

    constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly renderer: Renderer2
    ) {}

    ngAfterViewChecked(): void {
        const value = this.Pressed ? 'true' : 'false';
        if (value === this.written) return;
        this.written = value;
        this.renderer.setAttribute(this.host.nativeElement, 'aria-pressed', value);
    }
}
